import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { AtokuAccessRequest, AtokuService } from '../services/atoku.service';
import {
    DeclineAccessRequestDialogComponent,
    DeclineAccessRequestDialogResult
} from './dialogs/decline-access-request-dialog/decline-access-request-dialog.component';

interface AccessRequest {
    id: string | number;
    cardId: string;
    name: string;
    surname: string;
    email: string;
    spaceCode: string;
    reason: string;
    startDate: Date;
    startTime: string;
    endDate: Date;
    endTime: string;
}

// const MOCK_DATA: AccessRequest[] = [];

@Component({
    selector: 'app-atoku-access-requests',
    templateUrl: './atoku-access-requests.component.html',
    styleUrls: ['./atoku-access-requests.component.scss']
})
export class AtokuAccessRequestsComponent implements OnInit, AfterViewInit {
    displayedColumns: string[] = [
        'cardId',
        'name',
        'surname',
        'reason',
        'startDate',
        'startTime',
        'endDate',
        'endTime',
        'actions'
    ];
    dataSource = new MatTableDataSource<AccessRequest>([]);

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(
        private atokuService: AtokuService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.loadAccessRequests();
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
    }

    approveRequest(request: AccessRequest): void {
        this.atokuService.updateAccessRequestStatus(request.id, {
            status: 'approved',
            userEmail: request.email,
            spaceCode: request.spaceCode || 'main-kitchen',
            startDate: this.toUtcIso(request.startDate, request.startTime),
            endDate: this.toUtcIso(request.endDate, request.endTime),
            title: request.reason || 'temporary access'
        }).subscribe(() => {
            this.dataSource.data = this.dataSource.data.filter(item => item.id !== request.id);
        });
    }

    declineRequest(request: AccessRequest): void {
        const dialogRef = this.dialog.open<DeclineAccessRequestDialogComponent, AccessRequest, DeclineAccessRequestDialogResult>(
            DeclineAccessRequestDialogComponent,
            {
                width: '480px',
                data: request
            }
        );

        dialogRef.afterClosed().subscribe(result => {
            if (!result) return;

            this.atokuService.updateAccessRequestStatus(request.id, {
                status: 'declined',
                comment: result.reason
            }).subscribe(() => {
                this.dataSource.data = this.dataSource.data.filter(item => item.id !== request.id);
            });
        });
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    private loadAccessRequests(): void {
        this.atokuService.getAccessRequests().subscribe(requests => {
            this.dataSource.data = requests.map(request => this.mapAccessRequest(request));
        });
    }

    private mapAccessRequest(request: AtokuAccessRequest): AccessRequest {
        const raw = request as Record<string, unknown>;
        const fullName = String(raw['fullName'] ?? '').trim();
        const [name, ...surnameParts] = fullName.split(' ').filter(Boolean);
        const start = raw['startDateTime'] ? new Date(String(raw['startDateTime'])) : new Date();
        const end = raw['endDateTime'] ? new Date(String(raw['endDateTime'])) : new Date(start);
        return {
            id: String(raw['id'] ?? ''),
            cardId: String(raw['cardId'] ?? raw['clientReference'] ?? ''),
            name: name || fullName,
            surname: surnameParts.join(' '),
            email: String(raw['email'] ?? raw['userEmail'] ?? ''),
            spaceCode: String(raw['spaceCode'] ?? raw['subSpaceCode'] ?? raw['stationCode'] ?? ''),
            reason: String(raw['reason'] ?? ''),
            startDate: start,
            startTime: this.formatTime(start),
            endDate: end,
            endTime: this.formatTime(end)
        };
    }

    private formatTime(date: Date): string {
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
    }

    private toUtcIso(date: Date, time: string): string {
        const [hh, mm] = String(time || '00:00').split(':').map(v => Number(v || 0));
        const next = new Date(date);
        next.setHours(hh, mm, 0, 0);
        return next.toISOString();
    }
}
